import { useState, useRef, useEffect } from "react";
import "@/App.css";
import { Toaster } from "sonner";
import { TOOLS } from "@/tools";
import { ToolForm } from "@/components/ToolForm";
import { DocumentEditor } from "@/components/DocumentEditor";
import { BookOpenCheck } from "lucide-react";

function App() {
  const [activeId, setActiveId] = useState(TOOLS[0].id);
  const [result, setResult] = useState(null);
  const [common, setCommon] = useState({ skola: "", mjesto: "", duljina: "Standardno", ton: "Formalno-administrativni" });
  const editorRef = useRef(null);
  const active = TOOLS.find((t) => t.id === activeId);

  useEffect(() => {
    if (result && editorRef.current) {
      editorRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [result]);

  const selectTool = (id) => {
    setActiveId(id);
    setResult(null);
  };

  return (
    <div className="app-root">
      <Toaster position="bottom-center" richColors />

      <header className="app-header">
        <div className="header-inner">
          <div className="flex items-center gap-3">
            <div className="logo-badge" data-testid="app-logo">
              <BookOpenCheck className="w-5 h-5 text-white" />
            </div>
            <div>
              <span className="app-title">Stručko</span>
              <p className="app-subtitle">AI pomoćnik za stručne suradnike</p>
            </div>
          </div>
        </div>
      </header>

      <section className="hero">
        <div className="hero-inner">
          <h1 className="hero-title" data-testid="hero-title">
            Programi, mišljenja i zapisnici - <span className="hero-accent">gotovo u minuti</span>
          </h1>
          <p className="hero-sub">
            Odaberite alat, ispunite podatke, a umjetna inteligencija izradit će strukturirani
            službeni dokument koji možete urediti, kopirati i preuzeti kao Word ili PDF.
            Bez pohrane podataka - sve ostaje kod vas.
          </p>
        </div>
      </section>

      <main className="main-grid">
        <aside>
          <p className="step-label">1. Što vam treba?</p>
          <div className="tool-list" data-testid="tool-list">
            {TOOLS.map((t) => {
              const Icon = t.icon;
              const isActive = t.id === activeId;
              return (
                <button
                  key={t.id}
                  onClick={() => selectTool(t.id)}
                  data-testid={`tool-card-${t.id}`}
                  className={`tool-card ${isActive ? "tool-card-active" : ""}`}
                  style={isActive ? { borderColor: t.color, backgroundColor: t.colorLight } : {}}
                >
                  <span className="tool-icon" style={{ backgroundColor: t.colorLight, color: t.color, borderColor: t.colorBorder }}>
                    <Icon className="w-5 h-5" />
                  </span>
                  <span>
                    <span className="tool-name" style={isActive ? { color: t.color } : {}}>{t.name}</span>
                    <span className="tool-tagline">{t.tagline}</span>
                  </span>
                </button>
              );
            })}
          </div>
        </aside>

        <section>
          <p className="step-label">2. Ispunite podatke</p>
          <div className="form-panel">
            <ToolForm tool={active} common={common} setCommon={setCommon} onResult={setResult} />
          </div>

          {result && (
            <div ref={editorRef} className="mt-8">
              <p className="step-label">3. Uredite i preuzmite dokument</p>
              <DocumentEditor html={result.html} demo={result.demo} docTitle={active.docTitle} color={active.color} />
            </div>
          )}
        </section>
      </main>

      <footer className="app-footer">
        <p>
          Stručko · AI alat za stručne suradnike u odgoju i obrazovanju · Dokumenti se ne pohranjuju
          <br />
          Usklađeno sa{" "}
          <a
            href="https://mzom.gov.hr/UserDocsImages/dokumenti/Obrazovanje/Smjernice%20za%20rad%20s%20ucenicima%20s%20teskocama.pdf"
            target="_blank"
            rel="noopener noreferrer"
            className="underline hover:text-slate-600"
            data-testid="footer-smjernice-link"
          >
            Smjernicama za rad s učenicima s teškoćama (MZO)
          </a>
        </p>
      </footer>
    </div>
  );
}

export default App;
